import { Ionicons } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { LinearGradient } from "expo-linear-gradient";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, ScrollView, Share, StyleSheet, TouchableOpacity, View } from "react-native";
import Animated, { FadeIn, FadeInDown } from "react-native-reanimated";

import {
  DEMO_QUIZ_RESULT,
  DEMO_USER_STREAK,
  DEMO_USER_XP,
  isScreenshotMode,
} from "../../../../lib/screenshotMode";
import { getCategoryName } from "../../../constants/categoryMapping";
import { DSButton } from "../../../design-system/components/DSButton";
import { DSText } from "../../../design-system/components/DSText";
import { useAppTheme } from "../../../design-system/ThemeProvider";
import { animation, radius, spacing } from "../../../design-system/tokens";
import { t } from "../../../i18n/translations";
import { maybeRequestReviewAfterQuiz } from "../../../lib/appReview";
import { logAnalyticsEvent } from "../../../lib/firebase";
import { maybeRequestPushAfterQuiz } from "../../../lib/pushPrompt";
import { supabase } from "../../../lib/supabase";
import { RootStackParamList, SinglePlayerResultsData } from "../../../navigation/types";
import { GuestSignUpPrompt } from "../../../shared/components/GuestSignUpPrompt";
import { useGuestUser } from "../../../shared/contexts/GuestUserContext";
import {
  awardXP,
  evaluateAchievements,
  getUserStreak,
  getUserXP,
  newlyUnlockedAchievements,
  type AchievementEvaluation,
  type UserStreak,
  type UserXP,
} from "../../../shared/services/gamificationService";
import { achievementText } from "../../../shared/services/achievementText";
import { syncQuizToPGS } from "../../../shared/services/pgsAchievementBridge";
import { triggerAutoSave } from "../../../shared/services/pgsCloudSaveService";
import { iqesColors } from "../../../shared/ui/iqesDesignSystem";
import { useLanguageStore } from "../../quiz-core/languageStore";
import { useUserProfile } from "../../quiz-core/useStore";

const awardedQuizKeys = new Set<string>();

const getReflectionInsight = (resultsData: SinglePlayerResultsData | undefined): string | null => {
  if (!resultsData?.questions?.length) {
    return null;
  }

  const answers = resultsData.answers || [];

  for (let index = 0; index < resultsData.questions.length; index += 1) {
    const question = resultsData.questions[index];
    if (!question?.explanation) {
      continue;
    }

    if (answers[index] !== question.correct_answer) {
      return question.explanation;
    }
  }

  return resultsData.questions.find((question) => !!question.explanation)?.explanation || null;
};

const translateRankName = (rankName: string, language: string) => {
  const key = rankName.trim().toLowerCase();
  const translated = t(`gamification.${key}`, language);
  return translated === `gamification.${key}` ? rankName : translated;
};

const SinglePlayerResultsScreen: React.FC = () => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const navigation = useNavigation<StackNavigationProp<RootStackParamList, "SinglePlayerResultsScreen">>();
  const route = useRoute<RouteProp<RootStackParamList, "SinglePlayerResultsScreen">>();
  const screenshotMode = isScreenshotMode();
  const { resultsData } = route.params ?? {};
  const { id: userId, loading: userLoading } = useUserProfile();
  const { language } = useLanguageStore();
  const { isGuest, guestProgress } = useGuestUser();

  const [streakInfo, setStreakInfo] = useState<UserStreak | null>(screenshotMode ? DEMO_USER_STREAK : null);
  const [xpInfo, setXpInfo] = useState<UserXP | null>(screenshotMode ? DEMO_USER_XP : null);
  const [unlockedAchievements, setUnlockedAchievements] = useState<AchievementEvaluation[]>([]);
  const [showGuestPrompt, setShowGuestPrompt] = useState<boolean>(false);
  const [guestPromptVariant, setGuestPromptVariant] = useState<"soft" | "streak" | "achievement">("soft");

  const totalQuestions = screenshotMode ? DEMO_QUIZ_RESULT.totalQuestions : (resultsData?.totalQuestions ?? 10);
  const correctCount = screenshotMode
    ? DEMO_QUIZ_RESULT.score
    : (resultsData?.correctCount ?? resultsData?.correctAnswers ?? resultsData?.score ?? 0);
  const accuracy = useMemo(() => {
    const baseAccuracy = resultsData?.accuracy;
    if (typeof baseAccuracy === "number") {
      return baseAccuracy;
    }
    return totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
  }, [resultsData?.accuracy, correctCount, totalQuestions]);
  const categoryName = getCategoryName(resultsData?.categoryId ?? 1, language);
  const reflectionInsight = getReflectionInsight(resultsData);
  const scriptureDays = streakInfo?.current_streak || 0;
  const rankLabel = translateRankName(xpInfo?.rank || "Scholar", language);
  const levelLabel = xpInfo?.current_level ?? 1;
  const masteryProgress = Math.max(12, Math.min(100, accuracy));
  const progressStatusTitle = isGuest
    ? (t("results.storedOnDevice", language) || "Stored on this device")
    : (t("results.savedToJourney", language) || "Saved to your journey");
  const progressStatusBody = isGuest
    ? (t("results.localGuestProgress", language) || "Sign in when you are ready to keep this progress across devices.")
    : (t("results.categoryMasterySummary", language) || "Your latest session has been added to {{category}}.")
        .replace("{{category}}", categoryName);

  useEffect(() => {
    if (screenshotMode && typeof window !== "undefined") {
      (window as Window & { __screenshotReady?: string }).__screenshotReady = "03_results";
    }
  }, [screenshotMode]);

  useEffect(() => {
    if (screenshotMode || !isGuest) return;

    const checkGuestProgress = async () => {
      try {
        const countStr = await AsyncStorage.getItem("guest_quiz_count");
        const currentCount = countStr ? parseInt(countStr, 10) : 0;
        const newCount = currentCount + 1;
        await AsyncStorage.setItem("guest_quiz_count", newCount.toString());

        if (newCount === 3) {
          setGuestPromptVariant("soft");
          setShowGuestPrompt(true);
        } else if (newCount >= 6 && (newCount - 3) % 5 === 0) {
          setGuestPromptVariant("soft");
          setShowGuestPrompt(true);
        }
      } catch {
        // Guest prompt tracking is non-critical.
      }
    };

    checkGuestProgress();
  }, [isGuest, screenshotMode]);

  // Ask for a store rating after a strong session. Deliberately last in the
  // sequence: the guest sign-up prompt owns this moment when it fires, and two
  // modals stacked on one results screen would cost us both.
  //
  // streakInfo loads asynchronously (auth -> awardXP -> streak/XP fetch) and
  // is not guaranteed to resolve within the delay below. Depending on it here
  // means: if it arrives before the timer fires, the effect resets and the
  // freshest value is used; if it never arrives, the prompt still fires on
  // accuracy alone rather than waiting forever. Either way a just-hit streak
  // milestone gets a real chance to qualify instead of racing a fixed clock.
  // The quiz is the app's core action, so completing one is the single
  // engagement event worth sending to Firebase. Once per results screen.
  const completionLoggedRef = useRef(false);
  useEffect(() => {
    if (screenshotMode || !resultsData || completionLoggedRef.current) return;
    completionLoggedRef.current = true;
    void logAnalyticsEvent('quiz_complete', {
      mode: 'solo',
      accuracy_pct: Math.round(accuracy),
    });
  }, [screenshotMode, resultsData, accuracy]);

  const reviewPromptedRef = useRef(false);
  useEffect(() => {
    if (screenshotMode || !resultsData || showGuestPrompt || reviewPromptedRef.current) return;

    // The delay lets the score animation and confetti land first, and gives the
    // guest-prompt check above time to claim the moment if it wants it.
    const timer = setTimeout(() => {
      reviewPromptedRef.current = true;
      // Push permission first (once per install, after the first quiz); the rating prompt
      // stands down for this session if the push dialog was just shown.
      void maybeRequestPushAfterQuiz().then(() => maybeRequestReviewAfterQuiz(accuracy, streakInfo?.current_streak));
    }, 2500);

    return () => clearTimeout(timer);
  }, [screenshotMode, resultsData, showGuestPrompt, accuracy, streakInfo]);

  useEffect(() => {
    if (screenshotMode || userLoading || isGuest || !resultsData) return;

    const loadProgressContext = async () => {
      let currentUserId = userId;
      if (!currentUserId) {
        try {
          const {
            data: { user: authUser },
            error,
          } = await supabase.auth.getUser();
          if (authUser && !error) currentUserId = authUser.id;
        } catch {
          // Silent.
        }
      }
      if (!currentUserId) return;

      try {
        const rawScore = resultsData.correctCount ?? resultsData.correctAnswers ?? resultsData.score ?? 0;
        const total = resultsData.totalQuestions || 10;
        const questionIds = (resultsData.questions || []).map((q) => q.id).join(",");
        const quizKey = `${currentUserId}:${resultsData.categoryId}:${rawScore}:${total}:${questionIds}`;

        if (!awardedQuizKeys.has(quizKey)) {
          awardedQuizKeys.add(quizKey);
          const pct = Math.round((rawScore / total) * 100);
          let xpAmount = 10;
          if (pct >= 90) xpAmount += 15;
          if (pct === 100) xpAmount += 10;

          await awardXP(currentUserId, xpAmount, "quiz_completion", `Quiz completed with ${pct}% score`);
          // The result row was inserted by the submitting screen, so progress now
          // includes this quiz. Unlocks award their XP server-side.
          setUnlockedAchievements(newlyUnlockedAchievements(await evaluateAchievements(currentUserId)));

          syncQuizToPGS({
            correctCount: rawScore,
            totalQuestions: total,
            categoryId: resultsData.categoryId,
            gameType: "single",
          }).catch(() => {});
          triggerAutoSave(currentUserId).catch(() => {});
        }

        const [streak, xp] = await Promise.all([
          getUserStreak(currentUserId),
          getUserXP(currentUserId),
        ]);
        setStreakInfo(streak);
        setXpInfo(xp);
      } catch {
        // Results context should not block the screen.
      }
    };

    loadProgressContext();
  }, [isGuest, resultsData, screenshotMode, userId, userLoading]);

  const handleShare = useCallback(async () => {
    try {
      const message = (t("results.shareMessage", language) || "I scored {{score}}/{{total}} on the Bible Quiz!")
        .replace("{{score}}", correctCount.toString())
        .replace("{{total}}", totalQuestions.toString());
      await Share.share({ message });
    } catch {
      Alert.alert(t("results.shareFailed", language) || "Share failed");
    }
  }, [language, correctCount, totalQuestions]);

  const handleContinueJourney = useCallback(() => {
    navigation.navigate("Category");
  }, [navigation]);

  const handlePlayAgain = useCallback(() => {
    navigation.navigate("Category");
  }, [navigation]);

  const handleGuestSignUp = useCallback(() => {
    setShowGuestPrompt(false);
    navigation.navigate("Main");
  }, [navigation]);

  const handleCloseGuestPrompt = useCallback(() => {
    setShowGuestPrompt(false);
  }, []);

  const encouragementMessage = useMemo(() => {
    if (accuracy >= 90) return t("results.excellentPerformance", language) || "Wonderful! You know this well.";
    if (accuracy >= 70) return t("results.goodJob", language) || "Great progress on your journey!";
    return t("results.keepLearning", language) || "Every question is a step closer to understanding.";
  }, [accuracy, language]);

  const displayVerse =
    t("results.verseFightGoodFight", language) ||
    "I have fought the good fight, I have finished the race, I have kept the faith. — 2 Timothy 4:7";

  return (
    <View style={styles.screenContainer} testID="single-player-results-screen">
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
        bounces
      >
        <Animated.View entering={FadeIn.duration(animation.normal)} style={styles.heroSection}>
          <LinearGradient
            colors={[iqesColors.greenDeep, '#17351A', iqesColors.canvasRaised]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.heroCard}
          >
            <View style={styles.heroIconCircle}>
              <Ionicons name="sparkles" size={28} color={iqesColors.goldSoft} />
            </View>
            <DSText variant="label" color={iqesColors.goldSoft} align="center" uppercase style={styles.heroEyebrow}>
              {categoryName}
            </DSText>
            <DSText variant="display" color={iqesColors.ivory} align="center" style={styles.heroTitle}>
              {t("results.sessionComplete", language) || "Session Complete"}
            </DSText>
            <DSText variant="body" color="rgba(255,248,232,0.84)" align="center" style={styles.heroSubtitle}>
              {encouragementMessage}
            </DSText>

            <View style={styles.heroMetaRow}>
              <View style={styles.heroMetaChip}>
                <Ionicons name="flame" size={14} color={iqesColors.goldBright} />
                <DSText variant="caption" color={iqesColors.ivory} style={styles.heroMetaText}>
                  {scriptureDays} {t("results.daysWithScripture", language) || "days with Scripture this month"}
                </DSText>
              </View>
              <View style={styles.heroMetaChip}>
                <Ionicons name="ribbon" size={14} color={iqesColors.goldBright} />
                <DSText variant="caption" color={iqesColors.ivory} style={styles.heroMetaText}>
                  {rankLabel} · Lv {levelLabel}
                </DSText>
              </View>
            </View>
          </LinearGradient>
        </Animated.View>

        {unlockedAchievements.length > 0 && (
          <Animated.View entering={FadeInDown.delay(80).duration(animation.normal)} style={styles.unlockCard}>
            <DSText variant="caption" color={iqesColors.goldSoft} uppercase style={styles.sectionEyebrow}>
              {t("gamification.achievement_unlocked", language)}
            </DSText>
            {unlockedAchievements.map((item) => (
              <View key={item.achievement_id} style={styles.unlockRow}>
                <DSText variant="title" style={styles.unlockIcon}>{item.achievement.icon}</DSText>
                <View style={styles.unlockText}>
                  <DSText variant="label" color={iqesColors.ivory}>
                    {achievementText(item.achievement.achievement_key, "name", item.achievement.name, language)}
                  </DSText>
                  <DSText variant="caption" color="rgba(255,248,232,0.72)">
                    {achievementText(item.achievement.achievement_key, "description", item.achievement.description, language)}
                  </DSText>
                </View>
                <DSText variant="caption" color={iqesColors.goldBright}>
                  +{item.achievement.xp_reward} {t("gamification.xp_short", language)}
                </DSText>
              </View>
            ))}
          </Animated.View>
        )}

        <Animated.View entering={FadeInDown.delay(120).duration(animation.normal)} style={styles.summaryCard}>
          <View style={styles.summaryTopRow}>
            <View>
              <DSText variant="caption" color="rgba(255,248,232,0.68)" uppercase style={styles.sectionEyebrow}>
                {t("results.questionsExplored", language) || "questions explored"}
              </DSText>
              <View style={styles.scoreRow}>
                <DSText variant="display" color={iqesColors.goldSoft} style={styles.scoreValue}>
                  {correctCount}
                </DSText>
                <DSText variant="title" color="rgba(255,248,232,0.72)">
                  / {totalQuestions}
                </DSText>
              </View>
            </View>

            <View style={styles.accuracyBadge}>
              <Ionicons name="checkmark-circle" size={16} color="#9DF871" />
              <DSText variant="title" color={iqesColors.ivory}>
                {accuracy}%
              </DSText>
            </View>
          </View>

          <View style={styles.progressMetaRow}>
            <DSText variant="caption" color="rgba(255,248,232,0.66)" uppercase>
              {t("results.accuracy", language) || "Accuracy"}
            </DSText>
            <DSText variant="caption" color={iqesColors.goldSoft} uppercase>
              {rankLabel}
            </DSText>
          </View>
          <View style={styles.progressTrack}>
            <View style={[styles.progressFill, { width: `${masteryProgress}%` }]} />
          </View>
        </Animated.View>

        <Animated.View entering={FadeInDown.delay(150).duration(animation.normal)} style={styles.statusCard}>
          <View style={styles.cardHeaderRow}>
            <Ionicons
              name={isGuest ? "phone-portrait-outline" : "cloud-done-outline"}
              size={16}
              color={iqesColors.goldSoft}
            />
            <DSText
              variant="label"
              color={iqesColors.goldSoft}
              uppercase
              style={styles.cardHeaderLabel}
            >
              {progressStatusTitle}
            </DSText>
          </View>
          <DSText variant="body" color={iqesColors.ivory} style={styles.reflectionBody}>
            {progressStatusBody}
          </DSText>
          {isGuest && !!guestProgress?.quizzesCompleted && (
            <DSText variant="caption" color="rgba(255,248,232,0.66)" style={styles.statusMeta}>
              {`${guestProgress.quizzesCompleted} ${t("quizzes_completed_label", language) || "Quizzes Completed"}`}
            </DSText>
          )}
        </Animated.View>

        {!!reflectionInsight && (
          <Animated.View entering={FadeInDown.delay(180).duration(animation.normal)} style={styles.reflectionCard}>
            <View style={styles.cardHeaderRow}>
              <Ionicons name="book-outline" size={16} color={iqesColors.goldSoft} />
              <DSText variant="label" color={iqesColors.goldSoft} uppercase style={styles.cardHeaderLabel}>
                {t("results.insightHighlight", language) || "Something you learned"}
              </DSText>
            </View>
            <DSText variant="body" color={iqesColors.ivory} style={styles.reflectionBody}>
              {reflectionInsight}
            </DSText>
          </Animated.View>
        )}

        <Animated.View entering={FadeInDown.delay(240).duration(animation.normal)} style={styles.verseCard}>
          <View style={styles.cardHeaderRow}>
            <Ionicons name="sunny-outline" size={16} color={iqesColors.goldBright} />
            <DSText variant="label" color={iqesColors.goldSoft} uppercase style={styles.cardHeaderLabel}>
              {t("results.reflectionSubtitle", language) || "You spent time in God's Word today"}
            </DSText>
          </View>
          <DSText variant="body" color={iqesColors.ivory} style={styles.verseText}>
            {displayVerse}
          </DSText>
        </Animated.View>

        <Animated.View entering={FadeInDown.delay(300).duration(animation.normal)} style={styles.actionStack}>
          <DSButton
            testID="results-continue-journey"
            title={t("results.chooseNextQuiz", language) || "Choose Next Quiz"}
            variant="primary"
            onPress={handleContinueJourney}
            icon="arrow-forward"
            style={styles.primaryAction}
          />

          <View style={styles.secondaryActionRow}>
            <TouchableOpacity style={styles.secondaryAction} onPress={handlePlayAgain} activeOpacity={0.85}>
              <Ionicons name="refresh" size={18} color={iqesColors.goldBright} />
              <DSText variant="label" color={iqesColors.goldSoft} style={styles.secondaryActionText}>
                {t("results.playAgain", language) || "Play Again"}
              </DSText>
            </TouchableOpacity>

            <TouchableOpacity style={styles.secondaryAction} onPress={handleShare} activeOpacity={0.85}>
              <Ionicons name="share-social-outline" size={18} color={iqesColors.goldBright} />
              <DSText variant="label" color={iqesColors.goldSoft} style={styles.secondaryActionText}>
                {t("results.shareResults", language) || "Share Results"}
              </DSText>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </ScrollView>

      {isGuest && showGuestPrompt && !screenshotMode && (
        <GuestSignUpPrompt
          visible={showGuestPrompt}
          onClose={handleCloseGuestPrompt}
          onSignUp={handleGuestSignUp}
          variant={guestPromptVariant}
        />
      )}
    </View>
  );
};

const createStyles = (_colors: ReturnType<typeof useAppTheme>["colors"]) =>
  StyleSheet.create({
    screenContainer: {
      flex: 1,
      backgroundColor: iqesColors.canvas,
    },
    scrollContainer: {
      paddingHorizontal: spacing.xl,
      paddingTop: spacing.xl,
      paddingBottom: spacing["3xl"],
      gap: spacing.lg,
      backgroundColor: iqesColors.canvas,
    },
    heroSection: {
      marginTop: spacing.sm,
    },
    heroCard: {
      borderRadius: radius["2xl"],
      paddingHorizontal: spacing.xl,
      paddingVertical: spacing["2xl"],
      alignItems: "center",
      borderWidth: 1.3,
      borderColor: 'rgba(246,190,58,0.5)',
      shadowColor: iqesColors.gold,
      shadowOffset: { width: 0, height: 16 },
      shadowOpacity: 0.26,
      shadowRadius: 22,
      elevation: 10,
    },
    heroIconCircle: {
      width: 64,
      height: 64,
      borderRadius: 32,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(246,190,58,0.14)",
      borderWidth: 1,
      borderColor: 'rgba(246,190,58,0.42)',
      marginBottom: spacing.lg,
    },
    heroEyebrow: {
      letterSpacing: 1.3,
      marginBottom: spacing.sm,
    },
    heroTitle: {
      marginBottom: spacing.sm,
    },
    heroSubtitle: {
      opacity: 0.92,
      lineHeight: 22,
      marginBottom: spacing.lg,
    },
    heroMetaRow: {
      width: "100%",
      gap: spacing.sm,
    },
    heroMetaChip: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: spacing.xs,
      backgroundColor: "rgba(255,255,255,0.12)",
      borderRadius: radius.full,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
    },
    heroMetaText: {
      flexShrink: 1,
      textAlign: "center",
    },
    unlockCard: {
      backgroundColor: 'rgba(28,20,8,0.94)',
      borderRadius: radius.xl,
      padding: spacing.lg,
      borderWidth: 1,
      borderColor: 'rgba(246,190,58,0.55)',
      gap: spacing.sm,
    },
    unlockRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    unlockIcon: {
      width: 36,
      textAlign: 'center',
    },
    unlockText: {
      flex: 1,
    },
    summaryCard: {
      backgroundColor: 'rgba(8,10,24,0.92)',
      borderRadius: radius.xl,
      padding: spacing.xl,
      borderWidth: 1,
      borderColor: 'rgba(246,190,58,0.34)',
      shadowColor: iqesColors.gold,
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.16,
      shadowRadius: 16,
      elevation: 6,
    },
    summaryTopRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: spacing.lg,
      marginBottom: spacing.lg,
    },
    sectionEyebrow: {
      letterSpacing: 1.2,
      marginBottom: spacing.sm,
    },
    scoreRow: {
      flexDirection: "row",
      alignItems: "flex-end",
      gap: spacing.xs,
    },
    scoreValue: {
      lineHeight: 56,
    },
    accuracyBadge: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
      backgroundColor: 'rgba(255,255,255,0.08)',
      borderRadius: radius.full,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderWidth: 1,
      borderColor: 'rgba(255,255,255,0.16)',
    },
    progressMetaRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.sm,
    },
    progressTrack: {
      height: 10,
      borderRadius: radius.full,
      backgroundColor: 'rgba(255,255,255,0.14)',
      overflow: "hidden",
    },
    progressFill: {
      height: "100%",
      backgroundColor: iqesColors.goldBright,
      borderRadius: radius.full,
    },
    reflectionCard: {
      backgroundColor: 'rgba(8,10,24,0.92)',
      borderRadius: radius.xl,
      padding: spacing.xl,
      borderWidth: 1,
      borderColor: 'rgba(246,190,58,0.34)',
    },
    statusCard: {
      backgroundColor: 'rgba(8,10,24,0.92)',
      borderRadius: radius.xl,
      padding: spacing.xl,
      borderWidth: 1,
      borderColor: 'rgba(246,190,58,0.34)',
    },
    verseCard: {
      backgroundColor: 'rgba(67,44,15,0.9)',
      borderRadius: radius.xl,
      padding: spacing.xl,
      borderWidth: 1,
      borderColor: "rgba(246,190,58,0.42)",
    },
    cardHeaderRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.xs,
      marginBottom: spacing.md,
    },
    cardHeaderLabel: {
      letterSpacing: 1,
    },
    reflectionBody: {
      lineHeight: 24,
    },
    statusMeta: {
      marginTop: spacing.md,
    },
    verseText: {
      lineHeight: 24,
      fontStyle: "italic",
    },
    actionStack: {
      gap: spacing.md,
      marginTop: spacing.sm,
    },
    primaryAction: {
      minHeight: 56,
    },
    secondaryActionRow: {
      flexDirection: "row",
      gap: spacing.md,
    },
    secondaryAction: {
      flex: 1,
      minHeight: 52,
      borderRadius: radius.xl,
      borderWidth: 1,
      borderColor: 'rgba(246,190,58,0.34)',
      backgroundColor: 'rgba(8,10,24,0.92)',
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "row",
      gap: spacing.sm,
      paddingHorizontal: spacing.md,
    },
    secondaryActionText: {
      textAlign: "center",
    },
  });

export default SinglePlayerResultsScreen;
